import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateGuidelineMutation,
  useGetGuidelineQuery,
  usePostCommentMutation,
  useUpdateGuidelineMutation,
} from "../redux/Apis/Guideline";

const useCommunityController = (mode, externalUserId = null) => {
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useSelector((state) => state.language.lang);
  const { id } = useParams();
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const paramUserId = id;
  const stateUserId = location.state?.userId;
  const userId = externalUserId || stateUserId || paramUserId || null;
  const [discussions, setDiscussions] = useState([]);
  const [comment, setComment] = useState("");
  const [content, setContent] = useState("");
  const [isRefetching, setIsRefetching] = useState(false);

  const listQuery = useGetGuidelineQuery(
    {
      params: {
        lang,
        ...(userId ? { user: userId } : {}),
      },
    },
    {
      skip: mode !== "list",
      refetchOnMountOrArgChange: true,
    },
  );

  const detailsQuery = useGetGuidelineQuery(
    { id, params: { lang } },
    {
      skip: mode !== "details" && mode !== "post" ? true : !id,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );

  const [postComment, { isLoading: isPostingComment }] =
    usePostCommentMutation();
  const [createGuideline, { isSuccess: isPostSuccess, isLoading: isPosting }] =
    useCreateGuidelineMutation();
  const [
    updateGuideline,
    { isSuccess: isUpdateSuccess, isLoading: isUpdating },
  ] = useUpdateGuidelineMutation();

  useEffect(() => {
    if (mode === "list" && listQuery.isSuccess && listQuery.data?.data) {
      setDiscussions(listQuery.data.data);
      setIsRefetching(false);
    }
  }, [mode, listQuery.isSuccess, listQuery.data]);

  useEffect(() => {
    if (mode === "post" && id && detailsQuery.isSuccess) {
      setContent(detailsQuery.data?.data?.content || "");
      setIsRefetching(false);
    }
  }, [mode, id, detailsQuery.isSuccess, detailsQuery.data]);

  useEffect(() => {
    if (mode === "post" && (isPostSuccess || isUpdateSuccess)) {
      toast.success(`Guideline ${id ? "updated" : "posted"}`);
      setContent("");
      navigate("/community");
    }
  }, [isPostSuccess, isUpdateSuccess, navigate, id]);

  const onChangeComment = (e) => setComment(e?.target?.value);
  const onChangeContent = (e) => setContent(e?.target?.value);

  const onClickPostComment = async () => {
    if (!isLoggedIn) return toast.error("Login first to post a comment");
    if (!comment.trim())
      return toast.info("Please enter a comment before posting");

    try {
      const payload = { guideline: id, comment };
      const res = await postComment({ payload, params: { lang } }).unwrap();
      toast.success(res?.message || "Comment posted successfully");
      setComment("");
      setIsRefetching(true);
      await detailsQuery.refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to post comment");
    } finally {
      setIsRefetching(false);
    }
  };

  const onClickPostGuideline = async () => {
    if (!content.trim())
      return toast.error("Please enter content before posting");

    try {
      const payload = { content };
      if (id) {
        await updateGuideline({
          id,
          body: payload,
          params: { lang },
        }).unwrap();
      } else {
        await createGuideline({ payload, params: { lang } }).unwrap();
      }
      setIsRefetching(true);
      await listQuery.refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong");
    } finally {
      setIsRefetching(false);
    }
  };

  return {
    values: {
      ...(mode === "list" && {
        user,
        data: discussions,
        isLoading: listQuery.isLoading || listQuery.isFetching,
        isRefetching,
      }),
      ...(mode === "details" && {
        data: detailsQuery.data?.data || [],
        isLoading: detailsQuery.isLoading || isPostingComment,
        isRefetching,
        comment,
      }),
      ...(mode === "post" && {
        isLoading: isPosting || isUpdating,
        isRefetching,
        content,
        isEditing: !!id,
      }),
    },
    functions: {
      onChangeComment,
      onChangeContent,
      onClickPostComment,
      onClickPostGuideline,
      setDiscussions,
    },
    refetch: mode === "list" ? listQuery.refetch : detailsQuery.refetch,
  };
};

export default useCommunityController;
